import { fnv32a } from "../util/sample.js";

/**
 * The characters 复习 can ask a reader to put together, and how.
 *
 * `pnpm run hanzi:assemblies` derives the table from the 汉字 breakdown and the
 * stroke outlines and commits it as `./hanzi-assemblies.json`, and the handler
 * reads it here. `src/content/hanzi/hanzi-assembly.ts` has how each row is
 * derived, and docs/fuxi.md under "Putting a 汉字 together" has the card.
 *
 * **A row is four fields, to keep the file small.** The parts written one
 * after another, the decoys the same way, the boxes as four numbers each, and
 * which part owns each stroke as a digit per stroke.
 * The file goes into the handler's bundle, and an object per box would double
 * it.
 */

/** A box inside the character's square, in hundredths of its side. */
export interface AssemblyBox {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** What a card needs to put one character together. */
export interface HanziAssembly {
  /** The character. */
  readonly character: string;

  /** Its parts, in the order they are written. */
  readonly parts: readonly string[];

  /** The box each part fills, at the same places as {@link parts}. */
  readonly boxes: readonly AssemblyBox[];

  /**
   * The character's strokes each part owns, by their place in the writing
   * order, at the same places as {@link parts}. A part placed right is drawn
   * as these strokes of the character's own outline.
   */
  readonly strokes: readonly (readonly number[])[];

  /** Parts that are not in the character, shown among its own. */
  readonly decoys: readonly string[];
}

/**
 * One character's row: its parts, its decoys, the boxes flattened, and which
 * part owns each stroke, as one digit per stroke in writing order.
 */
export type AssemblyRow = readonly [string, string, readonly number[], string];

/** The committed file, keyed by character. */
export type AssemblyTableFile = Readonly<Record<string, AssemblyRow>>;

/**
 * One assembly as the file holds it.
 *
 * @param assembly - The derived assembly.
 * @returns Its row.
 */
export function assemblyRow(assembly: HanziAssembly): AssemblyRow {
  return [
    assembly.parts.join(""),
    assembly.decoys.join(""),
    assembly.boxes.flatMap(({ x, y, width, height }) => [x, y, width, height]),
    owners(assembly.strokes),
  ];
}

/**
 * The assembly a row holds.
 *
 * @param character - The character the row is keyed by.
 * @param row - The row.
 * @returns The assembly.
 */
export function assemblyOf(character: string, row: AssemblyRow): HanziAssembly {
  const [parts, decoys, flat, owned] = row;

  return {
    character,
    parts: Array.from(parts),
    decoys: Array.from(decoys),
    boxes: Array.from({ length: flat.length / 4 }, (_, at) => ({
      x: flat[at * 4]!,
      y: flat[at * 4 + 1]!,
      width: flat[at * 4 + 2]!,
      height: flat[at * 4 + 3]!,
    })),
    strokes: Array.from(parts, (_, part) =>
      Array.from(owned).flatMap((owner, stroke) =>
        owner === String(part) ? [stroke] : [],
      ),
    ),
  };
}

/** Which part owns each stroke, one digit per stroke in writing order. */
function owners(strokes: readonly (readonly number[])[]): string {
  const count = Math.max(-1, ...strokes.flat()) + 1;

  return Array.from({ length: count }, (_, stroke) =>
    String(strokes.findIndex((own) => own.includes(stroke))),
  ).join("");
}

/**
 * The tiles a card shows, the character's parts and its decoys together.
 *
 * Its own parts first and then the decoys, which is the order a posted tile's
 * number is read against before {@link shownTiles} shuffles it.
 *
 * @param assembly - The character's assembly.
 * @returns Every tile's characters.
 */
export function assemblyTiles(assembly: HanziAssembly): readonly string[] {
  return [...assembly.parts, ...assembly.decoys];
}

/**
 * The order the tiles stand in on a card, as places in {@link assemblyTiles}.
 *
 * Shuffled by the answer's own id, the way a word-order card's pieces are, so
 * a reload shows the tiles where they were and the handler can read a posted
 * tile number back. The parts never stand in their own order at the head of
 * the row, which would be the answer laid out: the shuffle is turned one
 * place where it lands there.
 *
 * @param properties - How many tiles there are, how many are the character's
 * own, and the answer's id.
 * @returns A place in {@link assemblyTiles} for each tile, in the order shown.
 */
export function shownTiles(properties: {
  readonly tiles: number;
  readonly parts: number;
  readonly eventId: string;
}): readonly number[] {
  const order = Array.from({ length: properties.tiles }, (_, at) => at);

  for (let at = order.length - 1; at > 0; at -= 1) {
    const other = fnv32a(`${properties.eventId}#${String(at)}`) % (at + 1);

    [order[at], order[other]] = [order[other]!, order[at]!];
  }

  const laidOut = order
    .slice(0, properties.parts)
    .every((place, at) => place === at);

  return laidOut ? [...order.slice(1), order[0]!] : order;
}

/**
 * The parts a reader chose, from the tile numbers their form posted.
 *
 * Each number is a tile's place in the row the card showed, which
 * {@link shownTiles} lays out again from the id. A number naming no tile, or
 * a tile chosen twice, cannot come of a form this site drew, and the press is
 * refused rather than marked.
 *
 * @param properties - The assembly, the answer's id and what was posted.
 * @returns The chosen parts in the order chosen, or nothing.
 */
export function chosenParts(properties: {
  readonly assembly: HanziAssembly;
  readonly eventId: string;
  readonly posted: string;
}): readonly string[] | undefined {
  const tiles = assemblyTiles(properties.assembly);
  const shown = shownTiles({
    tiles: tiles.length,
    parts: properties.assembly.parts.length,
    eventId: properties.eventId,
  });
  const places = properties.posted.trim().split(/\s+/u).filter(Boolean);

  if (
    places.some((place) => !/^\d+$/u.test(place)) ||
    new Set(places).size !== places.length
  ) {
    return undefined;
  }

  const chosen = places.map((place) => tiles[shown[Number(place)] ?? -1]);

  return chosen.every((part) => part !== undefined) ? chosen : undefined;
}

/**
 * The table, read once per handler.
 *
 * Built on first use rather than at import, the arrangement
 * `HandwritingStrokes` makes, so a cold start that only signs somebody in
 * pays nothing for it.
 */
export class HanziAssemblyTable {
  private readonly file: AssemblyTableFile;

  /**
   * @param file - The committed table, or a row or two of one in a test.
   */
  constructor(file: AssemblyTableFile) {
    this.file = file;
  }

  /**
   * Whether a card can ask for this character to be put together.
   *
   * The condition such a card is introduced under, beside the strokes a
   * handwriting card needs.
   */
  has(character: string): boolean {
    return Object.hasOwn(this.file, character);
  }

  /** How this character is put together, where the table has it. */
  of(character: string): HanziAssembly | undefined {
    const row = this.has(character) ? this.file[character] : undefined;

    return row === undefined ? undefined : assemblyOf(character, row);
  }
}
